import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
  AlertService,
  CampaignService,
  CheckoutsService,
  ImageService,
  LoaderService,
  SideNavService,
  StorageService,
} from '../../_services';
import { CampaignProduct } from '../../_models';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { CheckoutConfig, CheckoutConfigDefaults, BuilderProductType } from '../../_models';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Location } from '@angular/common';
import { ImageUploadComponent } from '../../image'
import { config } from '../../../config/config';

@Component({
  moduleId: module.id.toString(),
  selector: 'app-checkout-builder',
  templateUrl: './checkout-builder.component.html',
  styleUrls: ['./checkout-builder.component.scss'],
})
export class CheckoutBuilderComponent extends ImageUploadComponent implements OnInit, OnDestroy {
  selectedProducts: CampaignProduct[];
  selectedProductsInput: CampaignProduct[];
  productType: BuilderProductType;
  checkoutId: string | number;
  checkoutConfig: CheckoutConfig = CheckoutConfigDefaults;
  multipleProducts = false;
  campaignsLoading: boolean = true;
  isLoading = true;
  checkoutURL = environment.checkoutTemplatesUrl;
  public safeCheckoutURL: SafeResourceUrl;
  private iframe: HTMLIFrameElement | null = null;

  constructor(
    protected checkoutsService: CheckoutsService,
    private campaignService: CampaignService,
    protected loaderService: LoaderService,
    private sideNav: SideNavService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected location: Location,
    public modalService: NgxSmartModalService,
    protected sanitizer: DomSanitizer,
    protected imageService: ImageService,
    protected storageService: StorageService,

    protected alertService: AlertService
  ) {
    super(router, location, route, imageService, alertService, storageService, sanitizer);

    this.title = '';
    this.safeCheckoutURL = this.sanitizer.bypassSecurityTrustResourceUrl(this.checkoutURL);
    this.sideNav.setSideNavExpanded(false);
    this.route.params.subscribe((params) => {
      this.checkoutId = params['id'];
    });
    this.checkoutConfig.config = {
      build_mode: true,
      campaigns: [],
    };
    this.checkoutImage = true;
    super.ngOnInit();
  }

  ngOnInit(): void {
    document.body.classList.add('checkout-builder-active');
    this.loaderService.show();

    this.loaderService.loader$.subscribe((state) => {
      this.isLoading = state;
    });

    if (this.checkoutId) {
      this.checkoutsService.get(this.checkoutId).subscribe((checkout: any) => {
        this.checkoutConfig = {
          config: {
            build_mode: this.checkoutConfig.config.build_mode,
            campaigns: [],
          },
          checkout: {
            ...checkout,
            confirmation_title: checkout.confirmation_title || this.checkoutConfig.checkout.confirmation_title,
            confirmation_message: checkout.confirmation_message || this.checkoutConfig.checkout.confirmation_message,
          },
        };
        this.getCampaigns();
      });
    } else {
      this.getCampaigns();
    }
  }

  getCampaigns() {
    this.campaignService.list({page: 1, page_size: config.maxPageSize}).subscribe(
      (data) => {
        this.loaderService.hide();
        this.checkoutConfig.config.campaigns = data.results;
        this.campaignsLoading = false;

        setTimeout(() => {
          this.intializeIframe();
        });
      },
      (error) => {
        this.campaignsLoading = false;
        this.loaderService.hide();
      }
    );
  }

  intializeIframe() {
    this.iframe = document.getElementById('react-iframe') as HTMLIFrameElement;
    if (this.iframe) {
      this.iframe.addEventListener('load', () => {
        this.sendMessageToIframe(this.checkoutConfig);
      });
    }
  }

  sendMessageToIframe(data: any) {
    if (this.iframe && this.iframe.contentWindow) {
      this.iframe.contentWindow.postMessage(data, this.checkoutURL);
    }
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (event.origin === this.checkoutURL) {
      if (event.data) {
        /* Main Product */
        if (event.data.selectMainProduct) {
          this.productType = BuilderProductType.Main;
          this.selectedProductsInput = event.data.selectedProducts;
          this.modalService.getModal('selectProductsDialog').open();
          this.multipleProducts = false;
        } else if (event.data.selectUpsellProduct) {
          /* Upsell Card Product */
          this.productType = BuilderProductType.Upsell;
          this.selectedProductsInput = event.data.selectedProducts;
          this.modalService.getModal('selectProductsDialog').open();
          this.multipleProducts = false;
        } else if (event.data.selectPopupProduct) {
          /* Upsell Popup Product */
          this.productType = BuilderProductType.Popup;
          this.selectedProductsInput = event.data.selectedProducts;
          this.modalService.getModal('selectProductsDialog').open();
          this.multipleProducts = false;
        } else if (event.data.selectRelatedProducts) {
          /* Related Products */
          this.productType = BuilderProductType.Related;
          this.selectedProductsInput = event.data.selectedProducts;
          this.modalService.getModal('selectProductsDialog').open();
          this.multipleProducts = true;
        } else if (event.data.brand_logo || event.data.sales_banner || event.data.sales_banner_mobile) {
          /* Image Uploads */
          let imageData;
          if (event.data.brand_logo) {
            imageData = event.data.brand_logo;
          } else if (event.data.sales_banner) {
            imageData = event.data.sales_banner;
          } else {
            imageData = event.data.sales_banner_mobile;
          }

          const uploadFile = this.uploaderService.makeUploadFile(imageData.file, 0);
          this.onUploadOutput({
            type: 'addedToQueue',
            file: uploadFile,
          });

          setTimeout(() => {
            this.startUpload();
          }, 500);

          // Upload feedback
          this.uploadComplete.subscribe((result: any) => {
            if (result && result[0].file) {
              this.sendMessageToIframe({ imageUpdate: { key: imageData.key, file: result[0] } });
            }
          });
        } else if (event.data.updateCheckout || event.data.publishCheckout) {
          /* Deploy/Update Checkout */
          this.loaderService.show();
          if (event.data.updateCheckout) {
            this.checkoutsService.update(this.checkoutId, event.data.updateCheckout).subscribe(
              () => {
                this.loaderService.hide();
                this.alertService.success('Checkout Updated Successfully');
                this.router.navigate(['/checkouts']);
              },
              (error) => {
                this.loaderService.hide();
                this.alertService.error('Error Updating Checkout', error);
              }
            );
          } else if (event.data.publishCheckout) {
            this.checkoutsService.deploy(this.checkoutId, event.data.publishCheckout).subscribe(
              () => {
                this.loaderService.hide();
                this.alertService.success('Checkout Deployed Successfully');
                this.router.navigate(['/checkouts']);
              },
              (error) => {
                this.loaderService.hide();
                this.alertService.error('Error Deploying Checkout', error);
              }
            );
          }
        }
      }
    }
  }

  onProductsSelected(products: CampaignProduct[]) {
    this.selectedProducts = products;
    switch (this.productType) {
      case BuilderProductType.Main:
        this.sendMessageToIframe({ mainProduct: products[0] });
        break;
      case BuilderProductType.Upsell:
        this.sendMessageToIframe({ upsellProduct: products[0] });
        break;
      case BuilderProductType.Popup:
        this.sendMessageToIframe({ popupProduct: products[0] });
        break;
      case BuilderProductType.Related:
        this.sendMessageToIframe({ relatedProducts: products });
        break;
    }

    this.productType = null;
    this.multipleProducts = false;
    this.modalService.getModal('selectProductsDialog').close();
  }

  ngOnDestroy(): void {
    this.sideNav.setSideNavExpanded(true);
    document.body.classList.remove('checkout-builder-active');
  }
}
